import React, { useState, useEffect } from 'react';
import { fetchInstructions } from "../data/api.js";
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';

const ShoppingPrepList = ({ 
  mealOccurances, 
  meals 
}) => {
  const [instructions, setInstructions] = useState({});
  const [loading, setLoading] = useState(true);
  const shoppingList = new Map();
  const prepInstructions = new Map();

  useEffect(() => {
    const loadInstructions = async () => {
      try {
        const instructionsData = await fetchInstructions();
        setInstructions(instructionsData);
      } catch (error) {
        console.error('Error loading instructions:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInstructions();
  }, []);

  // Compile shopping and prep lists from all homes
  Object.entries(mealOccurances).forEach(([homeId, homeMealOccurances]) => {
    Object.entries(homeMealOccurances).forEach(([mealId, servingSizeCounts]) => {
      const meal = meals.find(m => m.id === mealId);
      if (!meal || !instructions[mealId]) return;

      // Add items for each serving size
      Object.entries(servingSizeCounts).forEach(([servingSize, count]) => {
        const mealInstructions = instructions[mealId][servingSize];
        if (!mealInstructions) return;

        // Add shopping list items
        mealInstructions.shoppingList.forEach(item => {
          const currentCount = shoppingList.get(item) || 0;
          shoppingList.set(item, currentCount + count);
        });

        // Add prep instructions
        const currentPrep = prepInstructions.get(mealId) || {
          name: meal.name,
          instructions: mealInstructions.prep,
          servings: {}
        };
        currentPrep.servings[servingSize] = (currentPrep.servings[servingSize] || 0) + count;
        prepInstructions.set(mealId, currentPrep);
      });
    });
  });

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  const generateShoppingPrepDocx = () => {
    // First, create the grouped shopping list
    const groupedShoppingList = Array.from(shoppingList).reduce((acc, [item, count]) => {
      const [department] = item.split(',');
      if (!acc[department]) acc[department] = [];
      acc[department].push({ item: item.split(',').slice(1).join(',').trim(), count });
      return acc;
    }, {});

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [new TextRun({ text: "Master Shopping List", bold: true, size: 32 })],
            spacing: { after: 400 }
          }),

          // Shopping List by Department
          ...Object.entries(groupedShoppingList).flatMap(([department, items]) => [
            new Paragraph({
              children: [new TextRun({ text: department, bold: true, size: 28 })],
              spacing: { before: 400, after: 200 }
            }),
            ...items.map(item => 
              new Paragraph({
                children: [
                  new TextRun({ text: `${item.item} × ${item.count}` })
                ],
                spacing: { before: 100 }
              })
            )
          ]),

          // Prep Instructions
          new Paragraph({
            children: [new TextRun({ text: "Master Prep List", bold: true, size: 32 })],
            spacing: { before: 800, after: 400 }
          }),

          ...Array.from(prepInstructions).flatMap(([mealId, mealData]) => [
            new Paragraph({
              children: [new TextRun({ text: mealData.name, bold: true, size: 28 })],
              spacing: { before: 400, after: 200 }
            }),
            ...Object.entries(mealData.servings).map(([servingSize, count]) =>
              new Paragraph({
                children: [new TextRun({ text: `${servingSize} servings (×${count})` })],
                spacing: { before: 100 }
              })
            ),
            new Paragraph({
              children: [new TextRun({ text: "Prep Instructions:", bold: true })],
              spacing: { before: 200 }
            }),
            ...mealData.instructions.map((instruction, idx) =>
              new Paragraph({
                children: [new TextRun({ text: `${idx + 1}. ${instruction}` })],
                spacing: { before: 100 }
              })
            )
          ])
        ]
      }]
    });

    return doc;
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex space-x-4">
      <button
          onClick={async () => {
            try {
              const doc = generateShoppingPrepDocx();
              const blob = await Packer.toBlob(doc);
              const formData = new FormData();
              formData.append('file', blob, 'shopping-prep-list.docx');

              const response = await fetch('https://hook.us2.make.com/iwk9zo0845y9d922bh6qsenrd2voyp2w', {
                method: 'POST',
                body: formData
              });

              if (!response.ok) {
                throw new Error('Failed to send file to Drive');
              }

              alert('Shopping & Prep List successfully sent to Drive!');
            } catch (error) {
              console.error("Error sending to Drive:", error);
              alert("Error sending Shopping & Prep List to Drive. Please try again.");
            }
          }}
          className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded"
        >
          Send to Drive
        </button>
        <button
          onClick={async () => {
            const doc = generateShoppingPrepDocx();
            const blob = await Packer.toBlob(doc);
            saveAs(blob, 'shopping-prep-list.docx');
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          Download Shopping & Prep List
        </button>
        
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Shopping List */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-xl font-semibold mb-4">Master Shopping List</h3>
          <div className="space-y-4">
            {/* Group items by department */}
            {Array.from(shoppingList).reduce((acc, [item, count]) => {
              const [department] = item.split(',');
              if (!acc[department]) acc[department] = [];
              acc[department].push({ item: item.split(',').slice(1).join(',').trim(), count });
              return acc;
            }, {}) && 
            Object.entries(
              Array.from(shoppingList).reduce((acc, [item, count]) => {
                const [department] = item.split(',');
                if (!acc[department]) acc[department] = [];
                acc[department].push({ item: item.split(',').slice(1).join(',').trim(), count });
                return acc;
              }, {})
            ).map(([department, items]) => (
              <div key={department} className="mb-4">
                <h4 className="font-medium text-lg mb-2">{department}</h4>
                <ul className="space-y-2">
                  {items.map((item, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <input type="checkbox" className="form-checkbox h-5 w-5 text-blue-600" />
                      <span className="flex-grow">{item.item}</span>
                      <span className="text-gray-600">×{item.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Prep List */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-xl font-semibold mb-4">Master Prep List</h3>
          <ul className="space-y-4">
            {Array.from(prepInstructions).map(([mealId, mealData]) => (
              <li key={mealId} className="border-b pb-4 last:border-b-0">
                <div className="font-medium text-lg mb-2">{mealData.name}</div>
                <div className="text-sm text-gray-600 mb-2">
                  {Object.entries(mealData.servings).map(([servingSize, count]) => (
                    <div key={servingSize}>
                      • {servingSize} servings (×{count})
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  <div className="font-medium mb-1">Prep Instructions:</div>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    {mealData.instructions.map((instruction, idx) => (
                      <li key={idx} className="text-sm">{instruction}</li>
                    ))}
                  </ol>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ShoppingPrepList;